import { createMask } from 'imask';
import React, { ChangeEvent, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaArrowLeft, FaArrowRight, FaIdCard } from 'react-icons/fa';
import sgrLockIcon from '../../assets/SGRLock_icon.png';
import { useSteps } from '../../contexts/stepContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import api from '../../services/api';
import { Title } from '../../styles/global';
import { phoneMask } from '../../utils/phoneMask';
import { errorToast } from '../../utils/toast';
import { Content, GoBack } from '../BodyStep/styles';
import Button from '../Button';
import Input from '../Input';
import Loading from '../Loading';
import { ButtonRow, FormContent, SkipButton } from './styles';

const maskZipCode = createMask({
  mask: '00.000-000',
});

const AdditionalFields: React.FC = () => {
  const [skipping, setSkipping] = useState(false);
  const {
    zipCode,
    phoneNumber,
    email,
    currentValidation,
    documentNum,
    birthday,
    name,
    documentType,
    selectedServices,
    handleSetZipCode,
    handleSetPhoneNumber,
    handleSetEmail,
    handleSetImage,
    handleChangeCurrentStep,
    handleReturnCurrentStep,
    handleJumpSteps,
    setResult,
    setMessageError,
    setServiceError,
  } = useSteps();

  const { handleSubmit } = useForm();
  const { translationPage } = useAppTranslation();
  const { tp } = translationPage(tr => tr.components.additionalFields);

  const validateFields = useCallback((): boolean => {
    if (zipCode === '') {
      errorToast({ message: tp('zipRequired') });
      return false;
    }
    if (phoneNumber === '') {
      errorToast({ message: tp('phoneRequired') });
      return false;
    }
    if (email === '') {
      errorToast({ message: tp('emailRequired') });
      return false;
    }
    return true;
  }, [email, phoneNumber, zipCode, tp]);

  const handleValidate = useCallback(() => {
    if (validateFields()) handleChangeCurrentStep();
  }, [validateFields, handleChangeCurrentStep]);

  const handleSkipAttachment = useCallback(async () => {
    if (!validateFields()) return;
    setSkipping(true);
    try {
      const blob = await fetch(sgrLockIcon).then(r => r.blob());
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      handleSetImage(base64);

      const { data } = await api.post('validationrequest', {
        document: documentNum,
        selfie: base64,
        birth_date: birthday,
        name,
        document_type: documentType,
        zip_code: zipCode || undefined,
        phone_number: phoneNumber || undefined,
        email: email || undefined,
        imageJwt: '',
        api_key: process.env.REACT_APP_API_KEY_UNICO,
        selected_services: selectedServices.map(s => s.cos_ser_id),
      });

      if (data.result) setResult(data.result);
      if (data.message) setMessageError(data.message);
      if (data.service) setServiceError(data.service);

      handleJumpSteps(3);
    } catch {
      errorToast({ message: 'Erro ao realizar consulta. Tente novamente.' });
    } finally {
      setSkipping(false);
    }
  }, [
    validateFields,
    documentNum,
    birthday,
    name,
    documentType,
    zipCode,
    phoneNumber,
    email,
    selectedServices,
    handleSetImage,
    handleJumpSteps,
    setResult,
    setMessageError,
    setServiceError,
  ]);

  const setZipCode = (e: ChangeEvent<HTMLInputElement>) => {
    e.target.value = maskZipCode.resolve(e.target.value);
    handleSetZipCode(e.target.value);
  };

  const setPhoneNumber = (e: ChangeEvent<HTMLInputElement>) => {
    handleSetPhoneNumber(phoneMask(e.target.value));
  };

  const isCNH = currentValidation === 'CNH';

  return (
    <>
      <Content>
        <GoBack onClick={handleReturnCurrentStep}>
          <FaArrowLeft size={16} />
          {tp('back')}
        </GoBack>
        <Title>{tp('title')}</Title>
        <FormContent onSubmit={handleSubmit(handleValidate)}>
          <Input
            type="text"
            label={tp('zipLabel')}
            onChange={e => setZipCode(e)}
            value={zipCode}
            width="40%"
            autoFocus
          />
          <Input
            type="tel"
            label={tp('phoneLabel')}
            onChange={e => setPhoneNumber(e)}
            value={phoneNumber}
            width="40%"
          />

          <Input
            type="email"
            label={tp('emailLabel')}
            onChange={e => handleSetEmail(e.target.value)}
            value={email}
            width="50%"
          />

          {isCNH ? (
            <ButtonRow>
              <Button title="Anexar imagem CNH" type="submit" icon={FaIdCard} />
              <SkipButton type="button" onClick={handleSkipAttachment}>
                Pular anexo
              </SkipButton>
            </ButtonRow>
          ) : (
            <Button title={tp('next')} type="submit" icon={FaArrowRight} />
          )}
        </FormContent>
      </Content>
      {skipping && <Loading />}
    </>
  );
};

export default AdditionalFields;
